import { useState } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Terminal, KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import { API, errMsg, setUserRole } from "../lib/api";
import { fmtDate } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { Chips } from "../components/Filters";
import { Panel } from "./Uploads";
import SettingsPage from "./SettingsPage";

const ROLE_LABEL = { admin: "Admin", user: "User", no_access: "No access" };
const ROLE_STYLE = {
  admin: "border-sky-500/40 bg-sky-500/15 text-sky-300",
  user: "border-emerald-500/40 bg-emerald-500/15 text-emerald-400",
  no_access: "border-red-500/40 bg-red-500/15 text-red-400",
};
const get = (path) => async () => (await axios.get(`${API}${path}`)).data;

const UserRow = ({ u, roles, onChange }) => {
  const change = async (role) => {
    try { await setUserRole(u.user_id, role); toast.success(`${u.email}: ${ROLE_LABEL[role]}`); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const id = u.email || u.user_id;
  return (
    <tr data-testid={`user-row-${id}`}>
      <td><div className="text-sm text-slate-100">{u.email || "—"}</div><div className="text-[11px] text-slate-500">{u.name}</div></td>
      <td><span data-testid={`user-role-${id}`} className={`inline-flex rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${ROLE_STYLE[u.role]}`}>{ROLE_LABEL[u.role]}</span></td>
      <td className="num text-xs text-slate-400">{fmtDate(u.created_at)}</td>
      <td className="num text-xs text-slate-400">{fmtDate(u.last_seen)}</td>
      <td>
        <div className="flex items-center gap-2">
          {u.role === "no_access" && <button data-testid={`approve-user-${id}`} onClick={() => change("user")} className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500">Give access</button>}
          {u.role === "user" && <button data-testid={`disable-user-${id}`} onClick={() => change("no_access")} className="rounded-md border border-red-500/50 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10">Disable</button>}
          <select data-testid={`role-select-${id}`} value={u.role} onChange={(e) => change(e.target.value)} className="h-8 rounded-sm border border-slate-700 bg-[#182232] px-2 text-xs text-slate-100">
            {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
          </select>
        </div>
      </td>
    </tr>
  );
};

const UsersTab = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["users"], queryFn: get("/users") });
  const refresh = () => qc.invalidateQueries({ queryKey: ["users"] });
  const users = data?.users || [];
  return (
    <div className="space-y-6">
      <div className="rounded-md border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-400">
        New people sign up on the sign-in page with their work email, then appear here as <span className="text-red-400">No access</span> until you give access.
        Disabled users keep their account but can't open the app. To invite, permanently remove or reset someone's password, use
        <span className="text-slate-200"> Manage → Users &amp; Auth</span> in Emergent (users can also use "Forgot password?" on the sign-in page).
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-900/60">
        <table className="data-table w-full text-sm" data-testid="users-table">
          <thead><tr><th>User</th><th>Access</th><th>Joined</th><th>Last seen</th><th>Actions</th></tr></thead>
          <tbody>{users.map((u) => <UserRow key={u.user_id} u={u} roles={data.roles} onChange={refresh} />)}</tbody>
        </table>
      </div>
    </div>
  );
};

const ActivityTab = () => {
  const { data = [] } = useQuery({ queryKey: ["activity"], queryFn: get("/activity") });
  const [q, setQ] = useState("");
  const list = data.filter((a) => !q || `${a.email} ${a.action} ${a.detail}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user, action, product…" className="h-9 w-72 rounded-md border border-slate-700 bg-[#182232] px-3 text-sm outline-none focus:border-sky-500" />
      <div className="max-h-[65vh] overflow-auto rounded-lg border border-slate-800 bg-slate-900/60">
        <table className="data-table w-full text-sm" data-testid="activity-table">
          <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Details</th></tr></thead>
          <tbody>
            {list.map((a) => (
              <tr key={a.id} data-testid={`activity-${a.id}`}>
                <td className="num text-xs text-slate-400">{fmtDate(a.at)}</td>
                <td className="text-xs text-slate-200">{a.email}</td>
                <td className="text-xs text-sky-300">{a.action}</td>
                <td className="max-w-[560px] truncate text-xs text-slate-400" title={a.detail}>{a.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <div data-testid="activity-empty" className="p-8 text-sm text-slate-500">No activity yet.</div>}
      </div>
    </div>
  );
};

const downloadScript = async () => {
  const text = (await (await fetch("/Import-EuroMaterials.ps1")).text()).replace("__APP_URL__", window.location.origin);
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
  Object.assign(document.createElement("a"), { href: url, download: "Import-EuroMaterials.ps1" }).click();
  URL.revokeObjectURL(url);
};

const ServerTab = () => {
  const { data, isLoading } = useQuery({ queryKey: ["upload-key"], queryFn: get("/admin/upload-key") });
  return (
    <Panel title="Automatic SAP upload from your server">
      {isLoading ? <div data-testid="upload-key-status-loading" className="mb-4 text-sm text-slate-500">Checking upload key…</div> : (
      <div data-testid="upload-key-status" className={`mb-4 flex items-center gap-2 text-sm ${data?.configured ? "text-emerald-400" : "text-amber-400"}`}>
        {data?.configured ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
        <KeyRound className="h-4 w-4" /> Upload key {data?.configured ? "is set — files without it are refused" : "is not set yet"}
      </div>
      )}
      <p className="mb-4 max-w-3xl text-sm text-slate-400">
        The PowerShell script reads the SAP central files Euro_Materials_1000.csv (plant 1000 – UK and IE products) and Euro_Materials_2000.csv (plant 2000 – FR products),
        keeps only the materials linked to Amazon products, takes Mat and FStk (free stock in pallets) and sends them here with the upload key. It skips the upload when the files haven't changed.
        Store the key on the server as the SAP_UPLOAD_KEY environment variable, set the two file paths at the top of the script, then schedule it with Windows Task Scheduler (e.g. hourly).
        To change the key, update SAP_UPLOAD_KEY in Emergent (Manage → Secrets) and on the server.
      </p>
      <button data-testid="download-ps-script-btn" onClick={downloadScript} className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-100 transition-colors hover:bg-slate-800">
        <Terminal className="h-4 w-4 text-sky-400" /> Download PowerShell script
      </button>
    </Panel>
  );
};

const TABS = { USERS: UsersTab, ACTIVITY: ActivityTab, SETTINGS: () => <SettingsPage embedded />, "SERVER UPLOAD": ServerTab };

export default function AdminPage() {
  const [tab, setTab] = useState("USERS");
  const Tab = TABS[tab];
  return (
    <div data-testid="admin-page">
      <PageHeader kicker="Admins only" title="Admin" />
      <div className="mb-6"><Chips options={Object.keys(TABS)} value={tab} onChange={setTab} testPrefix="admin-tab" /></div>
      <Tab />
    </div>
  );
}
